import React from 'react';
import { useTranslation } from 'react-i18next';
import { AIProgressPanel, AIRun, initialAIRun } from '../shared/brand/aiProgress';
import { GenerateMoreProgress } from '../../hooks/useGenerateMore';

interface FindMoreProgressProps {
  progress: GenerateMoreProgress;
  isGenerating: boolean;
}

/**
 * "Find more" generation, in place of the tab content while it runs.
 *
 * A thin adapter now: `useGenerateMore` keeps one flat line of progress, and
 * this maps it onto the shared AI run so the surface, the champagne bar and
 * the terminal states are the same ones every other AI process uses.
 */
const FindMoreProgress: React.FC<FindMoreProgressProps> = ({ progress, isGenerating }) => {
  const { t } = useTranslation();
  const show = isGenerating || progress.status === 'complete' || progress.status === 'failed';

  const run: AIRun = React.useMemo(() => {
    const failed = progress.status === 'failed';
    return {
      ...initialAIRun,
      status: failed
        ? 'failed'
        : progress.status === 'complete'
          ? 'completed'
          : progress.percent > 0 || progress.message
            ? 'running'
            : 'connecting',
      // The hook reports zero before the engine has said anything. A zero is
      // not a measurement, so it renders as the indeterminate sliver.
      percent: progress.percent > 0 ? Math.min(100, Math.round(progress.percent)) : null,
      message: failed ? null : progress.message || null,
      error: failed ? progress.message || t('Generation failed') : null,
    };
  }, [progress.status, progress.percent, progress.message, t]);

  if (!show) return null;

  return (
    <AIProgressPanel
      run={run}
      layout="centered"
      title={t('Generating...')}
      completedTitle={t('Generation complete')}
      failedTitle={t('Generation failed')}
      idleMessage={t('Processing...')}
      hint={<p className="text-xs text-fg-muted">{t('This may take up to a minute.')}</p>}
      className="min-h-[320px] py-16"
    />
  );
};

export default FindMoreProgress;
